import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="privacy-page">
      <p className="label-eyebrow mb-2">Privacy</p>
      <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-3">How we treat your data</h1>
      <p className="text-sm" style={{ color: "var(--text-2)" }}>
        Last updated {new Date().toLocaleDateString(undefined, { dateStyle: "long" })}
      </p>

      <Section title="In one paragraph">
        Alternavit is built around a simple principle: you tell us about your wellness concerns; we give you
        personalised, educational supplement guidance. We never sell your data, and we keep only what we need to
        make the product work. This page explains exactly what that means, in plain English.
      </Section>

      <Section title="Who we are">
        Alternavit is operated from the European Union. References to &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
        &ldquo;Alternavit&rdquo; mean the company that runs <a href="https://alternavit.com" className="underline" style={{ color: "var(--brand)" }}>alternavit.com</a>.
        For any privacy question, write to <a className="underline" style={{ color: "var(--brand)" }} href="mailto:privacy@alternavit.com">privacy@alternavit.com</a>.
      </Section>

      <Section title="What we collect">
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Account data:</strong> your name, email address, and hashed password.</li>
          <li><strong>Wellness assessment data:</strong> the answers you give in our questionnaires — age range, gender, lifestyle, sleep, stress, concerns and their severity. This is &ldquo;special category data&rdquo; under GDPR Article 9 and we process it only with your explicit consent.</li>
          <li><strong>Tracker logs:</strong> the supplements you mark as taken on which days.</li>
          <li><strong>Preferences:</strong> reminder settings, time zone, language.</li>
          <li><strong>Technical data:</strong> a session token in your browser, a small first-touch &ldquo;acquisition source&rdquo; record (which website referred you, any UTM parameters in the URL you used to arrive) so we can understand which channels reach people best, and minimal server logs for security and debugging.</li>
        </ul>
      </Section>

      <Section title="Why we collect it (legal basis)">
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Performance of a contract</strong> — to give you the service you signed up for (Art. 6(1)(b) GDPR).</li>
          <li><strong>Explicit consent</strong> for processing your wellness assessment data (Art. 9(2)(a) GDPR). You give this consent at signup and can withdraw it at any time.</li>
          <li><strong>Legitimate interest</strong> for security, fraud prevention, and product improvement (Art. 6(1)(f) GDPR).</li>
        </ul>
      </Section>

      <Section title="Who can see your data">
        Only you, and the small number of people who operate Alternavit. We use a handful of carefully-chosen sub-processors:
        <ul className="list-disc pl-5 space-y-2 mt-3">
          <li><strong>MongoDB Atlas</strong> &mdash; database hosting (EU region where available).</li>
          <li><strong>Anthropic (Claude)</strong> &mdash; AI-generated personalised explanations. Your data is sent transiently to generate the explanation and is not used to train their models.</li>
          <li><strong>Resend</strong> &mdash; transactional email delivery for reminders and digests (only if you opt in).</li>
        </ul>
        We do not sell, rent, or trade your data with anyone, for any purpose, ever.
      </Section>

      <Section title="How long we keep it">
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Account data:</strong> as long as you have an account. Deleting your account wipes it within 24 hours.</li>
          <li><strong>Wellness data and tracker logs:</strong> same as your account &mdash; gone the moment you delete.</li>
          <li><strong>Server logs:</strong> rotated after 30 days.</li>
        </ul>
      </Section>

      <Section title="Your rights">
        Under GDPR you have the right to:
        <ul className="list-disc pl-5 space-y-2 mt-3">
          <li><strong>Access</strong> &mdash; download a copy of everything we hold about you, from <Link to="/settings" className="underline" style={{ color: "var(--brand)" }}>Settings → Data &amp; account → Download my data</Link>.</li>
          <li><strong>Rectify</strong> &mdash; update inaccurate data; most of it is editable inside the app.</li>
          <li><strong>Erase (&ldquo;right to be forgotten&rdquo;)</strong> &mdash; delete your account and all related data from the same Settings page.</li>
          <li><strong>Restrict or object</strong> to certain processing.</li>
          <li><strong>Portability</strong> &mdash; the &ldquo;Download my data&rdquo; export is in machine-readable JSON.</li>
          <li><strong>Lodge a complaint</strong> with your national data-protection authority. A list is available at <a className="underline" href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer">edpb.europa.eu</a>.</li>
        </ul>
      </Section>

      <Section title="Cookies and similar storage">
        We use a single strictly-necessary item in <code>localStorage</code> to keep you signed in. Anything beyond that
        &mdash; analytics, marketing &mdash; is off by default and only enabled if you opt in via the cookie banner. You can
        change your mind by clearing your browser storage or contacting us.
      </Section>

      <Section title="A note on medical advice">
        Alternavit gives <em>educational</em> information about natural supplements. It is not a medical device, does
        not diagnose or treat conditions, and is not a substitute for the advice of a qualified healthcare
        professional. Please always consult one before starting a new supplement, especially if you take medication,
        are pregnant or nursing, or have a chronic condition.
      </Section>

      <Section title="Changes">
        If we ever change this policy materially, we&apos;ll notify you by email. Small clarifications will simply be reflected by an updated &ldquo;last updated&rdquo; date above.
      </Section>

      <p className="mt-12 text-sm">
        <Link to="/" className="underline" style={{ color: "var(--brand)" }}>← Back to home</Link>
      </p>
    </main>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-serif text-2xl mb-3">{title}</h2>
      <div className="leading-relaxed" style={{ color: "var(--text-2)" }}>{children}</div>
    </section>
  );
}
